import type { Container } from '@playcanvas/pcui';
import { Entity, Mat4, Quat, RotateGizmo, TranslateGizmo, Vec3 } from 'playcanvas';

import { Events } from '../events';
import { Scene } from '../scene';
import { areaSquareMeters, depthGeometry, flattenArea, type Point3 } from '../spatial-guides-data';
import { Splat } from '../splat';
import { ToolOverlay, OverlayWriter } from '../tool-overlay';
import { Transform } from '../transform';

type Kind = 'area' | 'depth';
type AreaEditMode = 'points' | 'move' | 'rotate';
type CoordSpace = 'local' | 'world';
type PointState = { measurePoints: Vec3[]; measureSelection: number; depthRoot?: Vec3 };
const mat = new Mat4();
const world = new Vec3();
const CLICK_TOLERANCE = 4;
const finite = (point: Vec3) => Number.isFinite(point.x) && Number.isFinite(point.y) && Number.isFinite(point.z);

// A reproducible polygon frame: X follows its first usable edge, Y is the
// polygon normal, and Z completes a right-handed frame. Degenerate drafts
// fall back to scene axes until they define a plane.
const polygonRotation = (points: Vec3[]) => {
    if (points.length < 3) return new Quat();
    const normal = new Vec3();
    const origin = points[0];
    for (let i = 1; i < points.length - 1; i++) {
        normal.add(new Vec3().cross(points[i].clone().sub(origin), points[i + 1].clone().sub(origin)));
    }
    if (!finite(normal) || normal.lengthSq() < 1e-16) return new Quat();
    normal.normalize();
    for (let i = 1; i < points.length; i++) {
        const x = points[i].clone().sub(origin);
        x.sub(normal.clone().mulScalar(x.dot(normal)));
        if (x.lengthSq() < 1e-16) continue;
        x.normalize();
        const z = new Vec3().cross(x, normal).normalize();
        return new Quat().setFromMat4(new Mat4().set([
            x.x, x.y, x.z, 0, normal.x, normal.y, normal.z, 0,
            z.x, z.y, z.z, 0, 0, 0, 0, 1
        ]));
    }
    return new Quat();
};

class TransformHandler {
    activate() {} deactivate() {}
}

class AdvancedMeasureTool {
    activate: () => void;
    deactivate: () => void;
    getFocus: () => { position: Vec3; radius: number } | null;

    constructor(events: Events, scene: Scene, canvasContainer: Container, kind: Kind) {
        const mode = `${kind}Measure`;
        const limit = kind === 'depth' ? 4 : 1000;
        let direction: 'front' | 'back' = 'front';
        let active = false;
        let transforming = false;
        let editMode: AreaEditMode = 'points';
        let coordSpace: CoordSpace = 'local';
        let wholeSelected = false;
        let dragPoints: Vec3[] = [];
        const dragCenter = new Vec3();
        const dragRotationInverse = new Quat();
        let revision = 0;
        let splat: Splat;
        let pointState: PointState;
        let state: { count: number; value: number; valid: boolean; direction: 'front' | 'back'; editMode: AreaEditMode; coordSpace: CoordSpace; planeLocked: boolean } = { count: 0, value: 0, valid: false, direction, editMode, coordSpace, planeLocked: false };
        events.function(`${mode}.state`, () => state);

        const getPoint = (index: number, out = new Vec3()) => splat.worldTransform.transformPoint(pointState.measurePoints[index], out);
        const points = () => pointState.measurePoints.map((_point, index) => getPoint(index));
        const packed = (point: Vec3): Point3 => [point.x, point.y, point.z];
        const depth = (pts: Vec3[]) => (pts.length === 4 ? depthGeometry({
            plane: pts.slice(0, 3).map(packed) as [Point3, Point3, Point3],
            point: packed(pts[3]),
            root: pointState?.depthRoot ? packed(splat.worldTransform.transformPoint(pointState.depthRoot)) : undefined,
            direction
        }) : null);
        const value = (pts: Vec3[]) => (kind === 'area' ? areaSquareMeters(pts.map(packed)) :
            depth(pts)?.distance ?? 0);

        const gizmo = new TranslateGizmo(scene.camera.camera, scene.gizmoLayer);
        gizmo.coordSpace = 'local';
        gizmo.colorAlpha = 0.65;
        let rotationGizmo: RotateGizmo;
        const wholeArea = () => kind === 'area' && editMode !== 'points';
        const centroid = (pts: Vec3[]) => pts.reduce((sum, point) => sum.add(point.clone().mulScalar(1 / pts.length)), new Vec3());
        const entity = new Entity(`${mode}GizmoPivot`);
        scene.app.root.addChild(entity);
        const transform = new Transform();
        const handler = new TransformHandler();
        const visible = () => {
            const guides = events.invokeOptional('spatialGuides');
            return guides?.measurementsVisible !== false && (kind === 'area' ? guides?.areaVisible !== false : guides?.depthVisible !== false);
        };
        const update = () => {
            gizmo.detach();
            rotationGizmo?.detach();
            if (active && visible() && splat && (wholeArea() ? wholeSelected && pointState.measurePoints.length >= 3 : pointState.measureSelection >= 0 && pointState.measureSelection < pointState.measurePoints.length + (kind === 'depth' && pointState.measurePoints.length === 4 ? 1 : 0))) {
                const pts = points();
                const geometry = kind === 'depth' ? depth(pts) : null;
                const point = wholeArea() ? centroid(pts) : geometry && pointState.measureSelection >= 3 ? new Vec3(pointState.measureSelection === 4 ? geometry.foot : geometry.end) : pts[pointState.measureSelection];
                const planeLocked = !!geometry && pointState.measureSelection >= 3;
                // Point the endpoint's positive axis away from the plane on the
                // selected side. Keep the root's in-plane axes stable when flipping.
                const rotation = planeLocked ? new Quat().setFromDirections(Vec3.UP, new Vec3(geometry.normal).mulScalar(pointState.measureSelection === 3 && direction === 'back' ? -1 : 1)) :
                    coordSpace === 'local' ? polygonRotation(kind === 'depth' ? pts.slice(0, 3) : pts) : Quat.IDENTITY;
                gizmo.coordSpace = planeLocked ? 'local' : coordSpace;
                if (rotationGizmo) rotationGizmo.coordSpace = coordSpace;
                transform.set(point, rotation, Vec3.ONE);
                events.invoke('pivot').place(transform);
                entity.setLocalPosition(point);
                entity.setLocalRotation(rotation);
                const selected = pointState.measureSelection;
                (['x', 'y', 'z', 'xy', 'xz', 'yz', 'xyz'] as const).forEach((axis) => {
                    gizmo.enableShape(axis, !geometry || selected < 3 || (selected === 4 ? ['x', 'z', 'xz'].includes(axis) : axis === 'y'));
                });
                (editMode === 'rotate' ? rotationGizmo : gizmo).attach([entity]);
            }
            const pts = splat ? points() : [];
            const measured = value(pts);
            state = { count: pts.length,
                value: measured,
                valid: kind === 'area' ? pts.length >= 3 && measured > 1e-8 : pts.length === 4 && measured > 1e-8,
                direction,
                editMode,
                coordSpace,
                planeLocked: kind === 'depth' && pts.length === 4 && pointState.measureSelection >= 3 };
            events.fire(`${mode}.state`, state);
            scene.forceRender = true;
        };

        const overlay = new ToolOverlay({
            lineColor: [0.282, 0.812, 0.796, 1],
            fillColor: [0.282, 0.812, 0.796, 0.18],
            transparentFill: true
        });
        overlay.provider = (writer: OverlayWriter) => {
            if (!splat || !visible()) return;
            const pts = points();
            const geometry = kind === 'depth' ? depth(pts) : null;
            if (geometry) pts[3].set(...geometry.end);
            pts.forEach(point => writer.dot(point));
            if (kind === 'area') {
                for (let i = 1; i < pts.length; i++) writer.segment(pts[i - 1], pts[i]);
                if (pts.length >= 3) {
                    writer.segment(pts[pts.length - 1], pts[0]);
                    for (let i = 1; i < pts.length - 1; i++) writer.fill(pts[0], pts[i], pts[i + 1]);
                }
            } else {
                if (pts.length >= 2) writer.segment(pts[0], pts[1]);
                if (pts.length >= 3) {
                    writer.segment(pts[1], pts[2]); writer.segment(pts[2], pts[0]); writer.fill(pts[0], pts[1], pts[2]);
                }
                if (pts.length === 4) {
                    if (geometry) {
                        writer.dot(new Vec3(geometry.foot));
                        writer.segment(new Vec3(geometry.foot), new Vec3(geometry.end));
                    }
                }
            }
        };
        scene.add(overlay);

        events.on(`${mode}.clear`, () => {
            if (!splat) return;
            revision++;
            editMode = 'points'; wholeSelected = false;
            pointState.depthRoot = undefined;
            pointState.measurePoints.length = 0; pointState.measureSelection = -1; update();
        });
        events.on(`${mode}.flatten`, () => {
            if (kind !== 'area' || !active || !splat || transforming) return;
            const flattened = flattenArea(points().map(packed));
            if (!flattened) {
                events.fire('measurement.error', 'Cannot flatten this area: place at least three non-collinear polygon points.');
                return;
            }
            mat.invert(splat.worldTransform);
            const localPoints = flattened.map(point => mat.transformPoint(new Vec3(point)));
            if (!localPoints.every(finite)) {
                events.fire('measurement.error', 'Cannot flatten this area with the current layer transform.');
                return;
            }
            revision++;
            pointState.measurePoints = localPoints;
            update();
            events.fire('measurement.error', 'Area flattened. Save to keep the change.');
        });
        events.on('depthMeasure.direction', (next: 'front' | 'back') => {
            if (kind !== 'depth' || !['front', 'back'].includes(next)) return;
            direction = next; update();
        });
        events.on(`${mode}.save`, (name: string) => {
            if (!active || !splat || !state.valid) return;
            events.fire('advancedMeasurement.save', kind, points(), name, direction, pointState.depthRoot ? splat.worldTransform.transformPoint(pointState.depthRoot) : undefined);
        });
        events.function(`${mode}.load`, (coordinates: Point3[], nextDirection: 'front' | 'back', root?: Point3) => {
            if (!splat) return false;
            revision++;
            mat.invert(splat.worldTransform);
            pointState.measurePoints = coordinates.map(point => mat.transformPoint(new Vec3(point)));
            pointState.measureSelection = 0;
            editMode = 'points'; wholeSelected = false;
            pointState.depthRoot = root ? mat.transformPoint(new Vec3(root)) : undefined;
            direction = nextDirection ?? 'front';
            update();
            return true;
        });
        events.on('measurement.deselect', () => {
            wholeSelected = false;
            if (pointState) pointState.measureSelection = -1;
            revision++;
            update();
        });
        events.on('spatialGuides.changed', () => {
            if (active) update(); scene.forceRender = true;
        });
        events.on('selection.changed', (selection: Splat) => {
            if (active) events.fire('tool.deactivate');
            splat = selection;
            pointState = selection && (kind === 'area' ? selection.areaMeasurePoints : selection.depthMeasurePoints);
            if (pointState) {
                pointState.measurePoints = pointState.measurePoints.filter(finite);
                pointState.measureSelection = Math.min(pointState.measureSelection, pointState.measurePoints.length - 1);
            }
            update();
        });
        events.on('pivot.moved', () => {
            if (active && splat && wholeArea() && transforming && dragPoints.length) {
                const pivot = events.invoke('pivot').transform;
                if (!finite(pivot.position)) return;
                mat.invert(splat.worldTransform);
                // Apply each drag against its original world-space snapshot, so rotation
                // remains rigid even under a rotated/non-uniformly scaled splat layer.
                const deltaRotation = new Quat().mul2(pivot.rotation, dragRotationInverse);
                const next = dragPoints.map((point) => {
                    const rotated = deltaRotation.transformVector(point.clone().sub(dragCenter));
                    return mat.transformPoint(rotated.add(pivot.position));
                });
                if (next.every(finite)) pointState.measurePoints = next;
                scene.forceRender = true;
                return;
            }
            if (!active || !splat || pointState.measureSelection < 0) return;
            const position = events.invoke('pivot').transform.position;
            if (!finite(position)) return;
            mat.invert(splat.worldTransform);
            const local = mat.transformPoint(position, new Vec3());
            if (finite(local)) {
                if (pointState.measureSelection === 4 && kind === 'depth') pointState.depthRoot = local;
                else pointState.measurePoints[pointState.measureSelection]?.copy(local);
            }
            scene.forceRender = true;
        });
        events.on('pivot.ended', update);
        let clicked = false;
        events.on('select.delete', () => {
            if (transforming || !active || !splat || pointState.measureSelection < 0) return;
            if (pointState.measureSelection === 4 && kind === 'depth') {
                pointState.depthRoot = undefined; pointState.measureSelection = -1; update(); return;
            }
            pointState.measurePoints.splice(pointState.measureSelection, 1);
            pointState.measureSelection = Math.min(pointState.measureSelection, pointState.measurePoints.length - 1);
            update();
        });
        const bindGizmo = (target: TranslateGizmo | RotateGizmo) => {
            target.on('transform:start', () => {
                transforming = true;
                clicked = false;
                revision++;
                dragPoints = wholeArea() ? points() : [];
                if (dragPoints.length) dragCenter.copy(centroid(dragPoints));
                dragRotationInverse.copy(entity.getLocalRotation()).invert();
                events.invoke('pivot').start();
            });
            target.on('transform:move', () => events.invoke('pivot').moveTRS(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale()));
            target.on('transform:end', () => {
                transforming = false;
                dragPoints = [];
                events.invoke('pivot').end();
            });
            target.on('render:update', () => {
                scene.forceRender = true;
            });
        };
        bindGizmo(gizmo);

        const updateGizmoSize = () => {
            const { camera, canvas } = scene;
            gizmo.size = (camera.ortho ? 820 / canvas.clientHeight : 880 / Math.max(canvas.clientWidth, canvas.clientHeight));
            if (rotationGizmo) rotationGizmo.size = gizmo.size;
        };
        updateGizmoSize();
        events.on('camera.resize', updateGizmoSize);
        events.on('camera.ortho', updateGizmoSize);
        events.on(`${mode}.coordSpace`, (next: CoordSpace) => {
            if (transforming || !['local', 'world'].includes(next)) return;
            coordSpace = next;
            revision++; clicked = false;
            update();
        });
        events.on('areaMeasure.editMode', (next: AreaEditMode) => {
            if (kind !== 'area' || !active || !splat || transforming || !['points', 'move', 'rotate'].includes(next) || (next !== 'points' && pointState.measurePoints.length < 3)) return;
            if (next === 'rotate' && !rotationGizmo) {
                rotationGizmo = new RotateGizmo(scene.camera.camera, scene.gizmoLayer);
                rotationGizmo.coordSpace = 'world';
                rotationGizmo.rotationMode = 'orbit';
                rotationGizmo.colorAlpha = 0.65;
                bindGizmo(rotationGizmo);
                updateGizmoSize();
            }
            revision++; clicked = false;
            editMode = next;
            wholeSelected = next !== 'points';
            pointState.measureSelection = -1;
            update();
        });

        let clickX = 0; let clickY = 0;
        const primary = (event: PointerEvent) => (event.pointerType === 'mouse' ? event.button === 0 : event.isPrimary);
        const down = (event: PointerEvent) => {
            if (!wholeArea() && !transforming && !clicked && primary(event)) {
                clicked = true; clickX = event.offsetX; clickY = event.offsetY;
            }
        };
        const move = (event: PointerEvent) => {
            if (clicked && Math.hypot(event.offsetX - clickX, event.offsetY - clickY) > CLICK_TOLERANCE) clicked = false;
        };
        const up = async (event: PointerEvent) => {
            if (wholeArea() || !splat || !visible() || !clicked || !primary(event)) return;
            clicked = false;
            const hitPoints = points();
            const geometry = kind === 'depth' ? depth(hitPoints) : null;
            if (geometry) {
                hitPoints[3].set(...geometry.end); hitPoints.push(new Vec3(geometry.foot));
            }
            for (let i = 0; i < hitPoints.length; i++) {
                scene.camera.worldToScreen(hitPoints[i], world);
                if (Math.abs(world.x * canvasContainer.dom.clientWidth - clickX) < 8 && Math.abs(world.y * canvasContainer.dom.clientHeight - clickY) < 8) {
                    pointState.measureSelection = i; update(); event.preventDefault(); event.stopPropagation(); return;
                }
            }
            if (pointState.measurePoints.length < limit) {
                const target = splat;
                const pickRevision = revision;
                const hit = await scene.camera.intersect(clickX / canvasContainer.dom.clientWidth, clickY / canvasContainer.dom.clientHeight);
                if (hit && finite(hit.position) && active && pickRevision === revision && splat === target && pointState.measurePoints.length < limit) {
                    mat.invert(splat.worldTransform); mat.transformPoint(hit.position, world);
                    if (!finite(world)) return;
                    pointState.measureSelection = pointState.measurePoints.length; pointState.measurePoints.push(world.clone()); update();
                }
            }
            event.preventDefault(); event.stopPropagation();
        };

        this.getFocus = () => {
            if (!splat || !pointState.measurePoints.length) return null;
            const pts = points(); const min = pts[0].clone(); const max = pts[0].clone();
            pts.slice(1).forEach((point) => {
                min.min(point); max.max(point);
            });
            const position = min.clone().add(max).mulScalar(0.5);
            return { position, radius: Math.max(max.clone().sub(min).length() * 0.5, scene.camera.distance * scene.camera.sceneRadius) };
        };
        this.activate = () => {
            editMode = 'points'; wholeSelected = false;
            active = true; update(); events.fire('transformHandler.push', handler);
            events.fire('measurement.ensureVisible', kind);
            canvasContainer.dom.addEventListener('pointerdown', down); canvasContainer.dom.addEventListener('pointermove', move); canvasContainer.dom.addEventListener('pointerup', up, true);
        };
        this.deactivate = () => {
            active = false; clicked = false; revision++;
            transforming = false; dragPoints = []; wholeSelected = false;
            if (pointState) pointState.measureSelection = -1;
            gizmo.detach(); events.fire('transformHandler.pop');
            canvasContainer.dom.removeEventListener('pointerdown', down); canvasContainer.dom.removeEventListener('pointermove', move); canvasContainer.dom.removeEventListener('pointerup', up, true); update();
        };
    }
}

export { AdvancedMeasureTool };
